import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import styles from './HeroSlide.module.css';

const fallbackImage = require('../../pages/Home/imgs/hero-image.png');

const isMobileViewport = () => typeof window !== 'undefined' && window.innerWidth < 768;

/**
 * HeroSlide - Individual slide for the hero carousel
 * Displays fullscreen background media (image/video) with clickable redirect
 *
 * The carousel mounts every slide (including two clones for the infinite wrap),
 * so `shouldLoad` gates the video element: without it every slide downloads its
 * file on page load. `isActive` drives playback, and `isSettled` delays pausing
 * until a slide transition has finished, so the outgoing slide does not freeze
 * while it is still on screen.
 */
const HeroSlide = ({
  card,
  index,
  totalSlides,
  onCtaClick,
  shouldLoad,
  isActive,
  isSettled
}) => {
  const [mediaError, setMediaError] = useState(false);
  const videoRef = useRef(null);

  const handleClick = useCallback(() => {
    if (onCtaClick && card) {
      const destination = card.url || '#';
      onCtaClick(card, destination);
    }
  }, [card, onCtaClick]);

  const isVideo = !!(
    card.extras?.fullScreenDesktopVideoUrl ||
    card.extras?.fullScreenMobileVideoUrl ||
    card.extras?.mobileVideoUrl ||
    card.extras?.desktopVideoUrl
  );

  /**
   * Only the fullscreen stills are usable as a poster: card.imageUrl is the
   * content-card banner (around 4.27:1) and would be badly cropped full-bleed.
   */
  const getPosterUrl = () => {
    if (isMobileViewport() && card.extras?.fullScreenMobileImageUrl) {
      return card.extras.fullScreenMobileImageUrl;
    }
    return card.extras?.fullScreenDesktopImageUrl || undefined;
  };

  const getMediaUrl = () => {
    const isMobile = isMobileViewport();

    // Fullscreen URLs (preferred for fullscreen carousel)
    if (isMobile && card.extras?.fullScreenMobileVideoUrl) {
      return card.extras.fullScreenMobileVideoUrl;
    }
    if (card.extras?.fullScreenDesktopVideoUrl) {
      return card.extras.fullScreenDesktopVideoUrl;
    }
    if (isMobile && card.extras?.fullScreenMobileImageUrl) {
      return card.extras.fullScreenMobileImageUrl;
    }
    if (card.extras?.fullScreenDesktopImageUrl) {
      return card.extras.fullScreenDesktopImageUrl;
    }

    // Regular URLs (fallback)
    if (isMobile && card.extras?.mobileVideoUrl) {
      return card.extras.mobileVideoUrl;
    }
    if (card.extras?.desktopVideoUrl) {
      return card.extras.desktopVideoUrl;
    }
    if (isMobile && card.extras?.mobileTemplateUrl) {
      return card.extras.mobileTemplateUrl;
    }
    if (card.extras?.desktopTemplateUrl) {
      return card.extras.desktopTemplateUrl;
    }
    if (isMobile && card.extras?.mobileImageUrl) {
      return card.extras.mobileImageUrl;
    }
    if (card.extras?.desktopImageUrl) {
      return card.extras.desktopImageUrl;
    }
    return card.imageUrl;
  };

  const handleMediaError = useCallback(() => {
    setMediaError(true);
  }, []);

  const mediaUrl = getMediaUrl();
  const fallbackUrl = fallbackImage;
  const displayUrl = mediaError ? fallbackUrl : mediaUrl;
  const showVideo = isVideo && mediaUrl && !mediaError && shouldLoad;

  useEffect(() => {
    const video = videoRef.current;

    if (!video) {
      return;
    }

    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute('muted', '');
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    if (isActive) {
      video.autoplay = true;
      video.setAttribute('autoplay', '');
      const played = video.play();
      if (played && played.catch) {
        played.catch(() => {});
      }
      return;
    }
    video.autoplay = false;
    video.removeAttribute('autoplay');

    if (isSettled) {
      video.pause();
    }
  }, [isActive, isSettled, showVideo]);

  /**
   * A video slide that has not been asked to load yet renders nothing, leaving
   * the dark backdrop behind it. Those slides are always off screen, so there is
   * nothing to see; images stay eager because they are small and swapping them
   * in late would flicker.
   */
  let media = null;

  if (showVideo) {
    media = (
      <video
        ref={videoRef}
        style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', top: 0, left: 0 }}
        src={mediaUrl}
        poster={getPosterUrl()}
        preload="auto"
        muted
        loop
        playsInline
        autoPlay={isActive}
        webkit-playsinline="webkit-playsinline"
        aria-hidden="true"
        onError={handleMediaError}
      />
    );
  } else if (!isVideo || mediaError) {
    media = (
      <img
        style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', top: 0, left: 0 }}
        src={displayUrl}
        alt=""
        aria-hidden="true"
        onError={handleMediaError}
      />
    );
  }

  const accessibleLabel = card.title
    ? `Slide ${index + 1} of ${totalSlides}: ${card.title}. Click to open ${card.url || 'link'}.`
    : `Slide ${index + 1} of ${totalSlides}. Click to open ${card.url || 'link'}.`;

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={accessibleLabel}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      style={{
        scrollSnapAlign: 'center',
        flexShrink: 0,
        width: '100%',
        height: '100vh',
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        cursor: card.url ? 'pointer' : 'default'
      }}
    >
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#1a1a1a'
      }}
      >
        {media}
      </div>
      <div className={styles.slideOverlay} />
    </div>
  );
};

HeroSlide.propTypes = {
  card: PropTypes.shape({
    id: PropTypes.string,
    imageUrl: PropTypes.string,
    url: PropTypes.string,
    title: PropTypes.string,
    extras: PropTypes.shape({
      mime: PropTypes.string,
      mobileVideoUrl: PropTypes.string,
      desktopVideoUrl: PropTypes.string,
      mobileTemplateUrl: PropTypes.string,
      desktopTemplateUrl: PropTypes.string,
      mobileImageUrl: PropTypes.string,
      desktopImageUrl: PropTypes.string,
      fullScreenMobileVideoUrl: PropTypes.string,
      fullScreenDesktopVideoUrl: PropTypes.string,
      fullScreenMobileImageUrl: PropTypes.string,
      fullScreenDesktopImageUrl: PropTypes.string
    })
  }).isRequired,
  index: PropTypes.number.isRequired,
  totalSlides: PropTypes.number.isRequired,
  onCtaClick: PropTypes.func,
  shouldLoad: PropTypes.bool,
  isActive: PropTypes.bool,
  isSettled: PropTypes.bool
};

HeroSlide.defaultProps = {
  onCtaClick: null,
  shouldLoad: true,
  isActive: true,
  isSettled: true
};

export default HeroSlide;
